import queryString from 'query-string'

import type {
  IMeasurement,
  TCreatedMeasurement,
  TMeasurementTree
} from '@web/entities/measurement'
import type { TUpdateMeasurementRequest } from '@web/entities/measurement/model/types/update-measurement-request.type'
import type { TLimits } from '@web/entities/work-mode/model/update-work-mode-dto.type'
import type { IWorkModeInfo } from '@web/entities/work-mode/model/work-mode-info.type'
import type { IWorkMode } from '@web/entities/work-mode/model/work-mode.interface'
import { ApiResource, ApiTag } from '@web/shared/constants/api.constants'
import { baseApi } from '@web/shared/infrastructure/api-base-query'
import type { TId } from '@web/shared/types/id.type'
import type { IPagination } from '@web/shared/types/paged-response.type'
import type { TQueryParams } from '@web/shared/types/query-params.type'

export const measurementsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMeasurements: builder.query<
      IPagination<IMeasurement>,
      TQueryParams<{
        measuringPointId?: TId
        workModeLevel?: number | null
      }>
    >({
      query: (params) => {
        const query = queryString.stringify({ ...params })
        return {
          url: `${ApiResource.Measurements}?${query}`
        }
      },
      providesTags: [ApiTag.Measurements]
    }),

    getMeasurementsTree: builder.query<TMeasurementTree[], void | null>({
      query: () => ({
        url: `${ApiResource.Measurements}/tree`,
        method: 'GET'
      }),
      providesTags: [ApiTag.MeasurementsTree]
    }),

    createMeasurement: builder.mutation<{ id: string }, TCreatedMeasurement>({
      query: (data) => ({
        url: ApiResource.Measurements,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [ApiTag.Measurements, ApiTag.MeasurementsTree]
    }),

    getMeasurement: builder.query<IMeasurement, { measurementId: TId }>({
      query: ({ measurementId }) => ({
        url: `${ApiResource.Measurements}/${measurementId}`
      })
    }),

    getWorkModeInfo: builder.query<IWorkModeInfo, { measurementId: TId }>({
      query: ({ measurementId }) => ({
        url: `${ApiResource.Measurements}/${measurementId}/workmode`
      }),
      providesTags: [ApiTag.WorkMode]
    }),

    updateWorkModeInfo: builder.mutation<
      IWorkMode,
      {
        measurementId: string
        data: TLimits
      }
    >({
      query: ({ measurementId, data }) => ({
        url: `${ApiResource.Measurements}/${measurementId}/workmode`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.WorkMode]
    }),

    updateMeasurementsStatus: builder.mutation<
      null,
      {
        updateData: { paused: boolean }
        measurementId: string
      }
    >({
      query: ({ updateData, measurementId }) => ({
        url: `${ApiResource.Measurements}/${measurementId}/paused`,
        method: 'PUT',
        body: updateData
      }),
      invalidatesTags: [ApiTag.GetAllNodes]
    }),

    updateMeasurement: builder.mutation<
      null,
      {
        measurementId: string
        body: TUpdateMeasurementRequest
      }
    >({
      query: ({ measurementId, body }) => ({
        url: `${ApiResource.Measurements}/${measurementId}`,
        method: 'PUT',
        body: body
      }),
      invalidatesTags: [ApiTag.Measurements]
    }),

    deleteMeasurement: builder.mutation<null, TId>({
      query: (id) => ({
        url: `${ApiResource.Measurements}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTag.MeasurementsTree]
    }),

    updateMeasurementReorder: builder.mutation<
      void,
      {
        measurementId: string
        data: { order: number; parentId: string | null }
      }
    >({
      query: ({ measurementId, data }) => ({
        url: `${ApiResource.Measurements}/${measurementId}/reorder`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: [ApiTag.MeasurementsTree, ApiTag.Measurements]
    })
  })
})

export const {
  useGetMeasurementsQuery,
  useLazyGetMeasurementQuery,
  useGetMeasurementsTreeQuery,
  useCreateMeasurementMutation,
  useGetMeasurementQuery,
  useLazyGetMeasurementsQuery,
  useUpdateMeasurementsStatusMutation,
  useGetWorkModeInfoQuery,
  useUpdateWorkModeInfoMutation,
  useUpdateMeasurementMutation,
  useUpdateMeasurementReorderMutation,
  useDeleteMeasurementMutation
} = measurementsApi
